/**
 * Custom hook to integrate the manual insertion of a link
 * - retrieve target info via quill.getLeaf(index)
 * schema
 * when a user
 */
import React from 'react';

import { translate } from '~l10n/client';
import Quill, { RangeStatic } from 'quill';

import { normalizer } from '~shared/components/quill/link/utilities';

export const useLink = ({
  quill,
  selection,
}: {
  quill: Quill;
  selection: RangeStatic;
}) => {
  // keep the window state selection is up-to-date
  React.useEffect(() => {
    if (!quill || !selection) return;
    if (selection.length > 0) {
      window['_selection'] = quill.getText(selection.index, selection.length);
      return;
    }
    if (window['_selection']) window['_selection'] = null;
  }, [quill, selection]);

  // reducer example
  interface InitialLinkState {
    originalLink: string;
    currentLink: string;
    originalLabel: string;
    currentLabel: string;
    start: number | null;
    end: number | null;
  }
  const initialLinkState = {
    originalLink: '',
    currentLink: '',
    originalLabel: '',
    currentLabel: '',
    start: null,
    end: null,
  };
  // composite actions
  type OnModalOpenClick = {
    type: 'OnModalOpenClick';
    payload: {
      currentLink: string;
      start: number;
      end: number;
      currentLabel: string;
      originalLabel: string;
    };
  };
  type SetCurrentLink = {
    type: 'SetCurrentLink';
    payload: { newValue: string };
  };
  type SetCurrentLabel = {
    type: 'SetCurrentLabel';
    payload: { newValue: string };
  };
  type SetOriginalLink = {
    type: 'SetOriginalLink';
    payload: { newValue: string };
  };
  // action type
  type StateActions =
    | SetCurrentLink
    | OnModalOpenClick
    | SetCurrentLabel
    | SetOriginalLink;
  const linkReducer = (
    state: InitialLinkState,
    action: StateActions
  ): InitialLinkState => {
    switch (action.type) {
      case 'SetCurrentLink': {
        const { newValue } = action.payload;
        return { ...state, currentLink: newValue };
      }
      case 'SetOriginalLink': {
        const { newValue } = action.payload;
        return { ...state, originalLink: newValue };
      }
      case 'SetCurrentLabel': {
        const { newValue } = action.payload;
        return { ...state, currentLabel: newValue };
      }
      case 'OnModalOpenClick': {
        return { ...state, ...action.payload };
      }
      default:
        return state;
    }
  };
  const [
    { currentLink, currentLabel, start, end, originalLabel, originalLink },
    dispatch,
  ] = React.useReducer(linkReducer, initialLinkState);

  // controlling highlighting of link button
  const [showLinkModal, setShowLinkModal] = React.useState(false);
  React.useEffect(() => {
    if (!quill || !selection) return;
    const { index } = selection;
    const linkValue = quill.getFormat(index, 1)?.link;
    dispatch({
      type: 'SetOriginalLink',
      payload: { newValue: linkValue ?? '' },
    });
  }, [quill, selection]);
  React.useEffect(() => {
    if (!quill || !selection) return;
    if (selection.length > 0) {
      window['_selection'] = quill.getText(selection.index, selection.length);
      return;
    }
    if (window['_selection']) window['_selection'] = null;
  }, [quill, selection]);
  // utilities
  const getExistingLink = React.useCallback(
    (index: number) => {
      if (!index === null) return null;
      return quill.getFormat(index, 1)?.link || null;
    },
    [quill]
  );
  const getFirstIndex = React.useCallback(
    (index) => {
      if (!index) return 0;
      for (let i = index; i >= 0; i--) {
        if (!quill.getContents(i, 1)?.ops[0]?.attributes?.link) {
          return i + 1;
        } else if (i === 0) {
          return 0;
        }
      }
    },
    [quill]
  );
  // get the last value of the link
  const getLastIndex = React.useCallback(
    (index) => {
      for (let i = index; i <= quill.getLength(); i++) {
        if (quill.getContents(i, 1)?.ops[0]?.attributes?.link) {
          continue;
        }
        return i;
      }
    },
    [quill]
  );
  // events
  const onLinkModalOpen = React.useCallback(() => {
    setShowLinkModal(true);
    const existingLink = getExistingLink(selection.index);
    let starting: number = null;
    let ending: number = null;
    if (existingLink) {
      // find its start and end
      starting = getFirstIndex(selection.index);
      ending = getLastIndex(selection.index);
    } else {
      starting = selection.index;
      ending = selection.index + selection.length;
    }
    const text = quill.getText(starting, ending - starting);
    // dispatcher
    dispatch({
      type: 'OnModalOpenClick',
      payload: {
        currentLink: existingLink || '',
        start: starting,
        end: ending,
        currentLabel: text,
        originalLabel: text,
      },
    });
  }, [getExistingLink, getFirstIndex, getLastIndex, quill, selection]);
  const onLinkModalClose = React.useCallback(() => {
    setShowLinkModal(false);
    dispatch({ type: 'SetCurrentLink', payload: { newValue: '' } });
  }, []);
  const onLabelChange = React.useCallback(
    (e) =>
      dispatch({
        type: 'SetCurrentLabel',
        payload: { newValue: e.target.value },
      }),
    []
  );
  const onLinkChange = React.useCallback(
    (e) =>
      dispatch({
        type: 'SetCurrentLink',
        payload: { newValue: e.target.value },
      }),
    []
  );
  const onLinkSave = React.useCallback(() => {
    quill.deleteText(start, end - start);
    quill.insertText(start, currentLabel, 'link', normalizer(currentLink));
  }, [currentLink, currentLabel, quill, start, end]);
  const onLinkDelete = React.useCallback(() => {
    quill.deleteText(start, end - start);
    quill.insertText(start, currentLabel);
  }, [start, end, currentLabel, quill]);
  return {
    showLinkModal,
    handleLinkButtonClick: onLinkModalOpen,
    handleLinkModalClose: onLinkModalClose,
    linkPlaceholder: translate('Enter a link'),
    currentLink,
    onLinkChange,
    label: currentLabel,
    onLabelChange,
    onLinkSave,
    onLinkDelete,
    isLinkSaveDisabled:
      !currentLink ||
      !currentLabel ||
      (originalLink === currentLink && originalLabel === currentLabel),
    originalLink,
  };
};
